// localStorage persistence for settings (schema v1).
// Graceful fallback on corruption / missing key.

import type { Settings } from '../types.js';
import type { Loadout } from '../sim/loadout.js';
import { normalizeLoadout } from '../sim/loadout.js';

const STORAGE_KEY = 'lumen-abyss-settings-v1';
// One-time migration marker: v1's default shipped show_touch_controls:false,
// which silently disabled ALL touch input on phones (see loadSettings).
const TOUCH_MIGRATION_KEY = 'lumen-abyss-touch-controls-v2';
// One-time migration marker: the desktop default render_scale used to be 1.0,
// but the pixelated half-res look is the intended art direction. Upgrade
// stored 1.0 → 0.5 once per device; re-raising it afterwards sticks.
const PIXEL_MIGRATION_KEY = 'lumen-abyss-pixel-scale-v3';
// Ship loadout (spec-loadout §1): `{ weapon, swift, bulwark, striker }`,
// written on loadout-screen confirm and applied on every restart.
const LOADOUT_STORAGE_KEY = 'la.loadout.v1';

/**
 * True on touch-capable devices (phones/tablets), false on desktop.
 * Safe outside the browser (tests/SSR): resolves to false without a navigator.
 */
export function detectTouchDevice(): boolean {
  return typeof navigator !== 'undefined' && (navigator.maxTouchPoints ?? 0) > 0;
}

export const DEFAULT_SETTINGS: Settings = {
  input: {
    bindings: {
      move_up: { primary: 'KeyW', alt: 'ArrowUp' },
      move_down: { primary: 'KeyS', alt: 'ArrowDown' },
      move_left: { primary: 'KeyA', alt: 'ArrowLeft' },
      move_right: { primary: 'KeyD', alt: 'ArrowRight' },
      dash: { primary: 'Space', alt: null },
      focus_aim: { primary: 'Mouse2', alt: null },
      pause: { primary: 'Escape', alt: null }
    },
    gamepad_deadzone: { inner: 0.18, outer: 0.92 }
  },
  video: {
    bloom_intensity: 1.0,
    colorblind_mode: 'off',
    crt_enabled: false,
    crt_scanlines: 0.5,
    crt_aberration: 0.3,
    crt_noise: 0.25,
    crt_barrel_warp: 0.15,
    crt_vhs_tracking: false,
    reduced_flashes: false,
    reduced_particles: false,
    reduced_shake: false,
    text_scale: 1.0,
    // Default: auto-detect touch device; false on desktop.
    mobile_performance: detectTouchDevice(),
    // Retro half-res everywhere by default: the chunky pixelated upscale IS
    // the art direction (and a big fragment-cost win). Users can raise it in
    // settings; the auto-scaler treats this value as its ceiling.
    render_scale: 0.5,
    // Auto dynamic resolution holds ~60 fps with all effects on.
    render_scale_auto: true,
    pixelated_upscale: true,
    // Captain portraits: procedural pixel characters by default.
    portrait_style: 'pixelchar',
    // Planet-surface curvature post-effect.
    planet_curvature: true,
    planet_curvature_strength: 0.25,
    // Lomo lens: vignette + edge blur (analogue camera feel).
    lomo_lens: true,
    lomo_lens_strength: 0.6,
    // VCR + rare glitch post-effect.
    vcr_glitch: true,
    vcr_strength: 0.5,
    vcr_glitch_strength: 0.8
  },
  audio: {
    master: 80,
    music: 70,
    sfx: 80
  },
  access: {
    pause_on_focus_loss: true,
    // On touch devices the touch adapter IS the input — it must default on
    // (v1's unconditional false disabled move/fire/dash on phones entirely).
    show_touch_controls: detectTouchDevice(),
    // Web Haptics feedback on touch devices by default.
    haptics_enabled: detectTouchDevice(),
    // First-run onboarding hints: show until the sequence completes once.
    onboarding_seen: false
  },
  targeting_policy_default: 'nearest'
};

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return structuredClone(DEFAULT_SETTINGS);
    const parsed = JSON.parse(raw) as Partial<Settings>;
    const merged = mergeDefaults(parsed);
    // Migration v2: v1's default persisted show_touch_controls:false for
    // anyone who ever saved settings, which disables ALL touch input on
    // phones. Upgrade stored false → true once per device; a deliberate
    // opt-out set afterwards sticks (the marker blocks re-migration).
    if (
      detectTouchDevice() &&
      parsed.access?.show_touch_controls === false &&
      localStorage.getItem(TOUCH_MIGRATION_KEY) === null
    ) {
      merged.access.show_touch_controls = true;
      localStorage.setItem(TOUCH_MIGRATION_KEY, '1');
      saveSettings(merged);
    }
    // Migration v3: desktop default render_scale was 1.0 (native); the
    // pixelated half-res look is the art direction. One-time upgrade of the
    // old default; a deliberate re-raise afterwards sticks (marker blocks
    // re-migration).
    if (
      !detectTouchDevice() &&
      parsed.video?.render_scale === 1.0 &&
      localStorage.getItem(PIXEL_MIGRATION_KEY) === null
    ) {
      merged.video.render_scale = 0.5;
      localStorage.setItem(PIXEL_MIGRATION_KEY, '1');
      saveSettings(merged);
    }
    return merged;
  } catch {
    return structuredClone(DEFAULT_SETTINGS);
  }
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Ignore quota / privacy-mode errors.
  }
}

/** True when a settings blob exists in localStorage (stored preferences present). */
export function hasStoredSettings(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Load the persisted ship loadout. Missing or corrupt data falls back to
 * DEFAULT_LOADOUT; out-of-range values are clamped/trimmed by
 * normalizeLoadout (unknown weapon → default, traits 0–4, ≤5 points total).
 */
export function loadLoadout(): Loadout {
  try {
    const raw = localStorage.getItem(LOADOUT_STORAGE_KEY);
    if (!raw) return normalizeLoadout(null);
    return normalizeLoadout(JSON.parse(raw));
  } catch {
    return normalizeLoadout(null);
  }
}

/** Persist the ship loadout (written on loadout-screen confirm). */
export function saveLoadout(loadout: Loadout): void {
  try {
    localStorage.setItem(LOADOUT_STORAGE_KEY, JSON.stringify(normalizeLoadout(loadout)));
  } catch {
    // Ignore quota / privacy-mode errors.
  }
}

function mergeDefaults(partial: Partial<Settings>): Settings {
  const base = structuredClone(DEFAULT_SETTINGS);
  if (partial.input) {
    base.input.gamepad_deadzone = partial.input.gamepad_deadzone ?? base.input.gamepad_deadzone;
    if (partial.input.bindings) {
      for (const [key, value] of Object.entries(partial.input.bindings)) {
        if (value && typeof value.primary === 'string') {
          base.input.bindings[key] = { primary: value.primary, alt: value.alt ?? null };
        }
      }
    }
  }
  if (partial.video) {
    base.video.bloom_intensity = clamp(partial.video.bloom_intensity ?? base.video.bloom_intensity, 0, 2);
    base.video.colorblind_mode = partial.video.colorblind_mode ?? base.video.colorblind_mode;
    base.video.crt_enabled = partial.video.crt_enabled ?? base.video.crt_enabled;
    base.video.crt_scanlines = clamp(partial.video.crt_scanlines ?? base.video.crt_scanlines, 0, 1);
    base.video.crt_aberration = clamp(partial.video.crt_aberration ?? base.video.crt_aberration, 0, 1);
    base.video.crt_noise = clamp(partial.video.crt_noise ?? base.video.crt_noise, 0, 1);
    base.video.crt_barrel_warp = clamp(partial.video.crt_barrel_warp ?? base.video.crt_barrel_warp, 0, 1);
    base.video.crt_vhs_tracking = partial.video.crt_vhs_tracking ?? base.video.crt_vhs_tracking;
    base.video.reduced_flashes = partial.video.reduced_flashes ?? base.video.reduced_flashes;
    base.video.reduced_particles = partial.video.reduced_particles ?? base.video.reduced_particles;
    base.video.reduced_shake = partial.video.reduced_shake ?? base.video.reduced_shake;
    base.video.text_scale = clamp(partial.video.text_scale ?? base.video.text_scale, 1, 1.5);
    // Absent in older blobs: falls back to the auto-detected default; an
    // explicit stored choice (either way) is preserved.
    base.video.mobile_performance = partial.video.mobile_performance ?? base.video.mobile_performance;
    base.video.render_scale = clamp(partial.video.render_scale ?? base.video.render_scale, 0.25, 2);
    base.video.render_scale_auto = partial.video.render_scale_auto ?? base.video.render_scale_auto;
    base.video.pixelated_upscale = partial.video.pixelated_upscale ?? base.video.pixelated_upscale;
    base.video.portrait_style = partial.video.portrait_style ?? base.video.portrait_style;
    base.video.planet_curvature = partial.video.planet_curvature ?? base.video.planet_curvature;
    base.video.planet_curvature_strength = clamp(partial.video.planet_curvature_strength ?? base.video.planet_curvature_strength, 0, 1);
    base.video.lomo_lens = partial.video.lomo_lens ?? base.video.lomo_lens;
    base.video.lomo_lens_strength = clamp(partial.video.lomo_lens_strength ?? base.video.lomo_lens_strength, 0, 1);
    base.video.vcr_glitch = partial.video.vcr_glitch ?? base.video.vcr_glitch;
    base.video.vcr_strength = clamp(partial.video.vcr_strength ?? base.video.vcr_strength, 0, 1);
    base.video.vcr_glitch_strength = clamp(partial.video.vcr_glitch_strength ?? base.video.vcr_glitch_strength, 0, 1);
  }
  if (partial.audio) {
    base.audio.master = clamp(partial.audio.master ?? base.audio.master, 0, 100);
    base.audio.music = clamp(partial.audio.music ?? base.audio.music, 0, 100);
    base.audio.sfx = clamp(partial.audio.sfx ?? base.audio.sfx, 0, 100);
  }
  if (partial.access) {
    base.access.pause_on_focus_loss = partial.access.pause_on_focus_loss ?? base.access.pause_on_focus_loss;
    base.access.show_touch_controls = partial.access.show_touch_controls ?? base.access.show_touch_controls;
    base.access.haptics_enabled = partial.access.haptics_enabled ?? base.access.haptics_enabled;
    base.access.onboarding_seen = partial.access.onboarding_seen ?? base.access.onboarding_seen;
  }
  if (partial.targeting_policy_default) {
    base.targeting_policy_default = partial.targeting_policy_default;
  }
  return base;
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
